import { useState, useEffect, useRef } from "react";
import { classNames } from "@NBCUDTC/reactjs-utility-belt/classnames";

import PeacockAvatar from "@/assets/img/peacock/avatar/default.png";
import { Image } from "@/components/atoms/Image";
import { MobileBottomNavItem } from "@/components/atoms/MobileBottomNavItem";

import type { MobileBottomNavProps } from "./types";
import { DEFAULT_BOTTOM_NAV } from "./configs";
import styles from "./MobileBottomNav.module.scss";

const INDICATOR_WIDTH = 40;

/**
 * Mobile Bottom Nav component that renders a navigation bar with nav items
 *
 * @param items - Navigation items to display
 * @param className - Additional class names for styling
 * @param attrs - Additional HTML attributes for the nav element
 * @returns the MobileBottomNav component
 *
 * @example
 * ```tsx
 * <MobileBottomNav
 *   items={[
 *     {
 *       iconName: StreamingIcons.HomeOutline,
 *       selectedIconName: StreamingIcons.HomeFilled,
 *       label: "Home",
 *     },
 *   ]}
 * />;
 * ```
 */
const MobileBottomNav = ({
  items = DEFAULT_BOTTOM_NAV,
  className,
}: MobileBottomNavProps) => {
  const [indicatorOffset, setIndicatorOffset] = useState(0);
  const selectedIndex = 0;
  const firstItemRef = useRef<HTMLButtonElement | null>(null);
  const cn = classNames(styles["mobile-bottom-nav"], className);

  const updateIndicatorOffset = () => {
    if (firstItemRef.current) {
      const itemWidth = firstItemRef.current.offsetWidth;
      if (itemWidth) {
        const offset =
          selectedIndex * itemWidth + itemWidth / 2 - INDICATOR_WIDTH / 2;
        setIndicatorOffset(offset);
      }
    }
  };

  useEffect(() => {
    if (!firstItemRef.current) return;

    const observer = new ResizeObserver(() => {
      updateIndicatorOffset();
    });
    observer.observe(firstItemRef.current);

    updateIndicatorOffset();

    return () => observer.disconnect();
  }, [selectedIndex]);

  return (
    <nav className={cn} role="navigation" aria-label="Bottom navigation">
      <div className={styles["nav-container"]}>
        {items.map((item, index) => (
          <MobileBottomNavItem
            key={index}
            ref={index === 0 ? firstItemRef : undefined}
            item={item}
            selected={index === selectedIndex}
          />
        ))}
        <button className={styles["avatar-container"]} aria-label="Profiles">
          <Image
            src={PeacockAvatar}
            allowColorOverride={false}
            className={styles["avatar"]}
            alt="User Avatar"
          />
        </button>
        <div
          className={styles["selection-indicator"]}
          style={{
            left: `${indicatorOffset}px`,
            width: `${INDICATOR_WIDTH}px`,
          }}
        />
      </div>
    </nav>
  );
};

export { MobileBottomNav };
